import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, ArrowLeft, Loader2, Inbox, Plus, Shield, Stethoscope, Sparkles, AlertTriangle, Clock } from "lucide-react";

const SlaBadge = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
    <Clock size={11} /> Non-urgent · response times may vary
  </span>
);
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const EmergencyBanner = () => (
  <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-foreground">
    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-destructive" />
    <p className="leading-relaxed">
      <span className="font-semibold text-destructive">Medical emergency?</span>{" "}
      Secure messaging is intended for non-urgent communication only and is not monitored 24/7. If this is an emergency, call{" "}
      <a href="tel:911" className="font-semibold text-destructive underline underline-offset-2">911</a>{" "}
      or go to the nearest ER. Response times may vary.
    </p>
  </div>
);

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  recipient_id: string;
  subject: string | null;
  body: string;
  is_read: boolean;
  created_at: string;
}

interface Conversation {
  conversation_id: string;
  subject: string | null;
  last_message: string;
  last_message_at: string;
  other_party_id: string;
  other_party_name: string;
  unread_count: number;
}

interface MessageInboxProps {
  /** The provider/admin ID this patient should message, or undefined for providers viewing all threads */
  recipientId?: string;
  recipientName?: string;
  /** If true, shows all conversations (admin mode) */
  isAdmin?: boolean;
}

const MessageInbox = ({ recipientId, recipientName, isAdmin }: MessageInboxProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [composing, setComposing] = useState(false);
  const [, setForceRender] = useState(0);
  const profileCacheRef = useRef<Record<string, string>>({});
  const roleCacheRef = useRef<Record<string, string>>({});

  const resolveProfileName = useCallback(async (userId: string): Promise<string> => {
    if (profileCacheRef.current[userId]) return profileCacheRef.current[userId];
    const [{ data: profileData }, { data: roleData }] = await Promise.all([
      supabase.from("profiles").select("first_name, last_name").eq("user_id", userId).single(),
      supabase.from("user_roles").select("role").eq("user_id", userId).limit(1),
    ]);
    const name = profileData
      ? `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim() || "Unknown"
      : "Unknown";
    const role = roleData?.[0]?.role || "user";
    profileCacheRef.current[userId] = name;
    roleCacheRef.current[userId] = role;
    // Force re-render so message bubbles pick up new names/roles
    setForceRender((n) => n + 1);
    return name;
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const { data: allMessages, error } = await supabase
      .from("messages")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !allMessages) {
      setLoading(false);
      return;
    }

    // Group by conversation_id
    const convMap = new Map<string, Message[]>();
    for (const msg of allMessages) {
      const list = convMap.get(msg.conversation_id) || [];
      list.push(msg);
      convMap.set(msg.conversation_id, list);
    }

    const convList: Conversation[] = [];
    for (const [convId, msgs] of convMap) {
      const latest = msgs[0]; // already sorted desc
      const otherPartyId = latest.sender_id === user.id ? latest.recipient_id : latest.sender_id;
      const unread = msgs.filter((m) => m.recipient_id === user.id && !m.is_read).length;
      const name = await resolveProfileName(otherPartyId);
      convList.push({
        conversation_id: convId,
        subject: msgs.find((m) => m.subject)?.subject || null,
        last_message: latest.body,
        last_message_at: latest.created_at,
        other_party_id: otherPartyId,
        other_party_name: name,
        unread_count: unread,
      });
    }

    convList.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());
    setConversations(convList);
    setLoading(false);
  }, [user, resolveProfileName]);

  const fetchThread = useCallback(async (conversationId: string) => {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (data) {
      // Resolve all unique sender profiles/roles BEFORE setting messages
      const uniqueSenders = [...new Set(data.map((m) => m.sender_id))];
      await Promise.all(uniqueSenders.map((id) => resolveProfileName(id)));
      setMessages(data);
      // Mark unread as read
      const unread = data.filter((m) => m.recipient_id === user?.id && !m.is_read);
      if (unread.length > 0) {
        await Promise.all(
          unread.map((m) =>
            supabase.from("messages").update({ is_read: true }).eq("id", m.id)
          )
        );
      }
    }
  }, [user, resolveProfileName]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("messages-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        (payload) => {
          const msg = payload.new as Message;
          if (msg.sender_id === user.id || msg.recipient_id === user.id) {
            fetchConversations();
            if (activeConversation === msg.conversation_id) {
              setMessages((prev) => [...prev, msg]);
              // Auto-mark as read
              if (msg.recipient_id === user.id) {
                supabase.from("messages").update({ is_read: true }).eq("id", msg.id);
              }
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, activeConversation, fetchConversations]);

  const openConversation = (convId: string) => {
    setActiveConversation(convId);
    setComposing(false);
    fetchThread(convId);
  };

  const handleSendReply = async () => {
    if (!user || !newMessage.trim() || !activeConversation) return;
    const conv = conversations.find((c) => c.conversation_id === activeConversation);
    if (!conv) return;

    setSending(true);
    const { error } = await supabase.from("messages").insert({
      conversation_id: activeConversation,
      sender_id: user.id,
      recipient_id: conv.other_party_id,
      body: newMessage.trim(),
    });
    if (error) {
      toast({ title: "Error", description: "Could not send message.", variant: "destructive" });
    } else {
      setNewMessage("");
    }
    setSending(false);
  };

  const handleNewMessage = async () => {
    if (!user || !recipientId || !newMessage.trim()) return;
    setSending(true);
    const convId = crypto.randomUUID();
    const { error } = await supabase.from("messages").insert({
      conversation_id: convId,
      sender_id: user.id,
      recipient_id: recipientId,
      subject: newSubject.trim() || null,
      body: newMessage.trim(),
    });
    if (error) {
      toast({ title: "Error", description: "Could not send message.", variant: "destructive" });
    } else {
      toast({ title: "Sent!", description: "Your message has been delivered." });
      setNewMessage("");
      setNewSubject("");
      setComposing(false);
      await fetchConversations();
      setActiveConversation(convId);
      fetchThread(convId);
    }
    setSending(false);
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  if (loading) {
    return (
      <>
        <EmergencyBanner />
        <Card className="border-border bg-card">
          <CardContent className="p-6 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 size={16} className="animate-spin" /> Loading messages…
          </CardContent>
        </Card>
      </>
    );
  }

  // Thread view
  if (activeConversation) {
    const conv = conversations.find((c) => c.conversation_id === activeConversation);
    return (
      <>
        <EmergencyBanner />
        <Card className="border-border bg-card">
        <CardContent className="p-0">
          {/* Thread header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Button variant="ghost" size="sm" onClick={() => { setActiveConversation(null); fetchConversations(); }}>
              <ArrowLeft size={16} />
            </Button>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">
                {conv?.subject || `Conversation with ${conv?.other_party_name || "Provider"}`}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <p className="text-xs text-muted-foreground">{conv?.other_party_name}</p>
                <SlaBadge />
              </div>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="h-[320px] px-4 py-3">
            <div className="space-y-3">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                const senderRole = roleCacheRef.current[msg.sender_id];
                const senderName = profileCacheRef.current[msg.sender_id];
                const isAdmin = senderRole === "admin";
                const isProvider = senderRole === "provider";
                const showRoleBadge = !isMe && (isAdmin || isProvider);

                return (
                  <div key={msg.id} className={cn("flex", isMe ? "justify-end" : "justify-start")}>
                    <div className={cn(
                      "max-w-[80%] rounded-2xl px-4 py-2.5",
                      isMe
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : isAdmin
                          ? "bg-amber-50 text-foreground rounded-bl-md border border-amber-200"
                          : isProvider
                            ? "bg-primary/10 text-foreground rounded-bl-md border border-primary/20"
                            : "bg-muted text-foreground rounded-bl-md"
                    )}>
                      {/* Sender role badge for incoming messages */}
                      {showRoleBadge && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          {isAdmin ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                              <Shield size={10} /> Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                              <Stethoscope size={10} /> Provider
                            </span>
                          )}
                          {senderName && (
                            <span className="text-[10px] text-muted-foreground">{senderName}</span>
                          )}
                        </div>
                      )}
                      {msg.subject && (
                        <p className={cn("text-xs font-semibold mb-1", isMe ? "text-primary-foreground/80" : "text-muted-foreground")}>
                          {msg.subject}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                      <p className={cn("text-[10px] mt-1", isMe ? "text-primary-foreground/60" : "text-muted-foreground")}>
                        {new Date(msg.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Reply input */}
          <div className="flex gap-2 px-4 py-3 border-t border-border">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a reply…"
              className="min-h-[40px] max-h-[100px] resize-none text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
            />
            <Button size="sm" onClick={handleSendReply} disabled={sending || !newMessage.trim()} className="self-end">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
        </CardContent>
        </Card>
      </>
    );
  }

  // Compose new message
  if (composing) {
    return (
      <>
        <EmergencyBanner />
        <Card className="border-border bg-card">
        <CardContent className="p-0">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <Button variant="ghost" size="sm" onClick={() => setComposing(false)}>
              <ArrowLeft size={16} />
            </Button>
            <p className="font-semibold text-sm text-foreground">New Message to {recipientName || "Provider"}</p>
          </div>
          <div className="p-4 space-y-3">
            <Input
              placeholder="Subject (optional)"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
            />
            <Textarea
              placeholder="Write your message…"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="min-h-[120px] text-sm"
            />
            <div className="flex justify-end">
              <Button onClick={handleNewMessage} disabled={sending || !newMessage.trim()} className="gap-2">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send Message
              </Button>
            </div>
          </div>
        </CardContent>
        </Card>
      </>
    );
  }

  // Conversation list
  return (
    <div className="mb-10">
      <EmergencyBanner />
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-lg text-foreground flex items-center gap-2">
            <MessageSquare size={20} />
            Messages
            {totalUnread > 0 && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0.5 ml-1">
                {totalUnread}
              </Badge>
            )}
          </h2>
          <SlaBadge />
        </div>
        {recipientId && (
          <Button size="sm" variant="outline" onClick={() => setComposing(true)} className="gap-1 text-xs">
            <Plus size={14} /> New Message
          </Button>
        )}
      </div>

      {conversations.length === 0 ? (
        <Card className="border-primary/15 bg-gradient-to-br from-primary/5 via-card to-secondary/30 overflow-hidden">
          <CardContent className="p-10 text-center">
            <div className="relative mx-auto mb-5 w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-primary/10 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center ring-4 ring-primary/5">
                <MessageSquare size={30} className="text-primary" strokeWidth={1.75} />
              </div>
              <Sparkles size={14} className="absolute -top-1 -right-1 text-primary/60" />
            </div>
            <h3 className="font-display text-xl text-foreground mb-2">Your inbox is quiet</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto leading-relaxed">
              {recipientId
                ? "Have a question about your treatment, dosing, or side effects? Send a secure message to your care team."
                : "Messages from your provider and care team will show up here."}
            </p>
            {recipientId && (
              <Button size="lg" onClick={() => setComposing(true)} className="gap-2 shadow-sm">
                <Send size={16} /> Send your first message
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <Card
              key={conv.conversation_id}
              className={cn(
                "border-border bg-card cursor-pointer hover:shadow-md transition-shadow",
                conv.unread_count > 0 && "border-primary/30 bg-primary/5"
              )}
              onClick={() => openConversation(conv.conversation_id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn("text-sm truncate", conv.unread_count > 0 ? "font-bold text-foreground" : "font-medium text-foreground")}>
                        {conv.subject || conv.other_party_name}
                      </p>
                      {conv.unread_count > 0 && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    {conv.subject && (
                      <p className="text-xs text-muted-foreground">{conv.other_party_name}</p>
                    )}
                    <p className="text-xs text-muted-foreground truncate mt-1">{conv.last_message}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {new Date(conv.last_message_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageInbox;
